import * as React from 'react';
import {
    Dialog,
    DialogFooter,
    DialogType,
    DefaultButton,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    MessageBar,
    MessageBarType,
    IModalProps,
    mergeStyles,
} from '@fluentui/react';
import { LanguageContext } from '../utilities/LocalizationModule';

interface ConfirmDialogProps {
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => Promise<void> | void;
    onDismiss: () => void;
    confirmText?: string;
    dismissText?: string;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    isOpen,
    title,
    message,
    onConfirm,
    onDismiss,
    confirmText,
    dismissText,
}) => {
    const languageStrings = React.useContext(LanguageContext);
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [submitError, setSubmitError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (isOpen) {
            setIsSubmitting(false);
            setSubmitError(null);
        }
    }, [isOpen]);

    const dialogContentProps = React.useMemo(() => ({
        type: DialogType.normal,
        title,
        closeButtonAriaLabel: languageStrings.Close,
        subText: message,
    }), [title, message, languageStrings]);

    const modalProps: IModalProps = React.useMemo(() => ({
        isBlocking: true,
        overlay: {
            className: mergeStyles({ transition: 'all ease 1s' }),
        },
    }), []);

    const handleConfirm = async () => {
        if (isSubmitting) return;
        setSubmitError(null);
        setIsSubmitting(true);
        try {
            await onConfirm();
            // On success, the parent is expected to close the dialog (by flipping the
            // isOpen prop). If it doesn't, we simply remain open with no busy state.
        } catch (err: any) {
            const apiMsg = err?.response?.data?.message ?? err?.response?.data;
            const fallback = typeof err?.message === 'string' ? err.message : null;
            setSubmitError(
                (typeof apiMsg === 'string' && apiMsg) || fallback || languageStrings.UnexpectedError
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog
            hidden={!isOpen}
            onDismiss={() => { if (!isSubmitting) onDismiss(); }}
            dialogContentProps={dialogContentProps}
            modalProps={modalProps}
            minWidth={640}
            maxWidth={640}
        >
            {submitError && (
                <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginBottom: 12 } }}>
                    {submitError}
                </MessageBar>
            )}
            <DialogFooter>
                <PrimaryButton onClick={handleConfirm} disabled={isSubmitting}>
                    {confirmText ?? languageStrings.Confirm}
                    {isSubmitting && <Spinner size={SpinnerSize.small} style={{ marginLeft: 12 }} />}
                </PrimaryButton>
                <DefaultButton
                    onClick={() => onDismiss()}
                    disabled={isSubmitting}
                    text={dismissText ?? languageStrings.Cancel}
                />
            </DialogFooter>
        </Dialog>
    );
};

export default ConfirmDialog;
